import {
  localReceiptDatePrefix,
  nextReceiptId,
  receiptIdPrefixForDate,
} from '../local-receipt-id.js';
import { reconcileHistoryEntry, withReturnedQuantities } from '../sales-history.js';
import type { OfflineReceiptHistoryEntry, OfflineSaleItem } from '../types.js';
import type { SqliteDatabase } from './database.js';

type SalesHistoryRow = {
  id: string;
  sale_visible_id: string;
  receipt_number: string;
  cart_id: string;
  branch_id: string;
  cashier_name: string;
  items_json: string;
  subtotal: number;
  discount_percentage: number;
  discount_amount: number;
  grand_total: number;
  payment_method: string;
  cash_given: number;
  cash_returned: number;
  created_at: string;
  status: string;
  pending_sync: number;
};

function rowToSalesHistoryEntry(row: SalesHistoryRow): OfflineReceiptHistoryEntry {
  return {
    id: row.id,
    saleVisibleId: row.sale_visible_id,
    receiptNumber: row.receipt_number,
    cartId: row.cart_id,
    branchId: row.branch_id,
    cashierName: row.cashier_name,
    items: JSON.parse(row.items_json),
    subtotal: row.subtotal,
    discountPercentage: row.discount_percentage,
    discountAmount: row.discount_amount,
    grandTotal: row.grand_total,
    paymentMethod: row.payment_method,
    cashGiven: row.cash_given,
    cashReturned: row.cash_returned,
    createdAt: row.created_at,
    status: row.status as 'COMPLETED' | 'REFUNDED',
    pendingSync: row.pending_sync === 1,
  };
}

export function getSalesHistoryEntryByCartId(
  db: SqliteDatabase,
  cartId: string,
): OfflineReceiptHistoryEntry | undefined {
  const row = db
    .prepare(`SELECT * FROM sales_history WHERE cart_id = ? LIMIT 1`)
    .get(cartId) as SalesHistoryRow | undefined;
  return row ? rowToSalesHistoryEntry(row) : undefined;
}

/** Next local receipt id in RCP-YYYYMMDD-XXXX form (continues after last known local/BE id). */
export function allocateLocalReceiptId(db: SqliteDatabase, now = new Date()): string {
  const datePrefix = localReceiptDatePrefix(now);
  const like = `${receiptIdPrefixForDate(datePrefix)}%`;
  const row = db
    .prepare(
      `SELECT receipt_number FROM sales_history
       WHERE receipt_number LIKE ?
       ORDER BY receipt_number DESC LIMIT 1`,
    )
    .get(like) as { receipt_number: string } | undefined;
  return nextReceiptId(datePrefix, row?.receipt_number);
}

export function findSalesHistoryByReceiptRef(
  db: SqliteDatabase,
  ...refs: Array<string | undefined>
): OfflineReceiptHistoryEntry | undefined {
  const ids = [...new Set(refs.filter((r): r is string => Boolean(r && r.trim())))];
  if (ids.length === 0) return undefined;
  for (const id of ids) {
    const row = db
      .prepare(
        `SELECT * FROM sales_history
         WHERE receipt_number = ? OR sale_visible_id = ?
         LIMIT 1`,
      )
      .get(id, id) as SalesHistoryRow | undefined;
    if (row) return rowToSalesHistoryEntry(row);
  }
  return undefined;
}

/**
 * Map a UI/outbox receipt reference to the current history receipt after BE ack.
 * Uses sales_history first, then receipt_id_aliases for remapped provisional ids.
 */
export function resolveHistoryReceiptRefs(
  db: SqliteDatabase,
  originalReceiptId: string,
  originalSaleId: string,
): { receiptNumber: string; saleVisibleId: string } {
  const hist = findSalesHistoryByReceiptRef(db, originalReceiptId, originalSaleId);
  if (hist) {
    return { receiptNumber: hist.receiptNumber, saleVisibleId: hist.saleVisibleId };
  }

  const alias = db
    .prepare(
      `SELECT receipt_number, sale_visible_id FROM receipt_id_aliases
       WHERE old_id IN (?, ?) LIMIT 1`,
    )
    .get(originalReceiptId, originalSaleId) as
    | { receipt_number: string; sale_visible_id: string }
    | undefined;

  if (alias) {
    return {
      receiptNumber: alias.receipt_number,
      saleVisibleId: alias.sale_visible_id,
    };
  }

  return { receiptNumber: originalReceiptId, saleVisibleId: originalSaleId };
}

function rememberReceiptAlias(
  db: SqliteDatabase,
  oldId: string,
  mapping: { receiptNumber: string; saleVisibleId: string; cartId: string },
): void {
  if (!oldId || oldId === mapping.receiptNumber || oldId === mapping.saleVisibleId) return;
  db.prepare(
    `INSERT INTO receipt_id_aliases (old_id, receipt_number, sale_visible_id, cart_id)
     VALUES (?, ?, ?, ?)
     ON CONFLICT(old_id) DO UPDATE SET
       receipt_number = excluded.receipt_number,
       sale_visible_id = excluded.sale_visible_id,
       cart_id = excluded.cart_id`,
  ).run(oldId, mapping.receiptNumber, mapping.saleVisibleId, mapping.cartId);
}

/** Merge a BE-pulled row with any local row (pending sync / offline refund wins). */
export function mergePulledSalesHistoryEntry(
  db: SqliteDatabase,
  pulled: OfflineReceiptHistoryEntry,
): void {
  const local = getSalesHistoryEntryByCartId(db, pulled.cartId);
  const merged = reconcileHistoryEntry(local, pulled);
  upsertSalesHistoryEntry(db, local ? { ...merged, id: local.id } : merged);
}

export function upsertSalesHistoryEntry(
  db: SqliteDatabase,
  entry: OfflineReceiptHistoryEntry,
): void {
  db.prepare(
    `INSERT INTO sales_history (
      id, sale_visible_id, receipt_number, cart_id, branch_id, cashier_name,
      items_json, subtotal, discount_percentage, discount_amount, grand_total,
      payment_method, cash_given, cash_returned, created_at, status, pending_sync
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    ON CONFLICT(id) DO UPDATE SET
      sale_visible_id = excluded.sale_visible_id,
      receipt_number = excluded.receipt_number,
      cashier_name = CASE
        WHEN excluded.cashier_name <> '' THEN excluded.cashier_name
        ELSE sales_history.cashier_name
      END,
      status = excluded.status,
      pending_sync = excluded.pending_sync`,
  ).run(
    entry.id,
    entry.saleVisibleId,
    entry.receiptNumber,
    entry.cartId,
    entry.branchId,
    entry.cashierName,
    JSON.stringify(entry.items),
    entry.subtotal,
    entry.discountPercentage,
    entry.discountAmount,
    entry.grandTotal,
    entry.paymentMethod,
    entry.cashGiven,
    entry.cashReturned,
    entry.createdAt,
    entry.status,
    entry.pendingSync ? 1 : 0,
  );
}

export function getBranchSalesHistorySql(
  db: SqliteDatabase,
  branchId: string,
): OfflineReceiptHistoryEntry[] {
  const rows = db
    .prepare(
      `SELECT * FROM sales_history WHERE branch_id = ? ORDER BY created_at DESC LIMIT 500`,
    )
    .all(branchId) as SalesHistoryRow[];

  return rows.map((r) => rowToSalesHistoryEntry(r));
}

export function ackSyncedSaleInHistorySql(
  db: SqliteDatabase,
  cartId: string,
  ack: { receiptId: string; saleVisibleId: string },
): void {
  const existing = db
    .prepare(`SELECT receipt_number, sale_visible_id FROM sales_history WHERE cart_id = ?`)
    .get(cartId) as { receipt_number: string; sale_visible_id: string } | undefined;

  db.prepare(
    `UPDATE sales_history SET
      receipt_number = ?, sale_visible_id = ?, pending_sync = 0
     WHERE cart_id = ?`,
  ).run(ack.receiptId, ack.saleVisibleId, cartId);

  if (existing) {
    const mapping = {
      receiptNumber: ack.receiptId,
      saleVisibleId: ack.saleVisibleId,
      cartId,
    };
    rememberReceiptAlias(db, existing.receipt_number, mapping);
    rememberReceiptAlias(db, existing.sale_visible_id, mapping);
    rewirePendingRefundReceipts(db, {
      oldReceiptId: existing.receipt_number,
      oldSaleVisibleId: existing.sale_visible_id,
      newReceiptId: ack.receiptId,
      newSaleVisibleId: ack.saleVisibleId,
    });
  }
}

/** After sale ack, pending refunds may still reference the provisional local receipt id. */
export function rewirePendingRefundReceipts(
  db: SqliteDatabase,
  mapping: {
    oldReceiptId: string;
    oldSaleVisibleId: string;
    newReceiptId: string;
    newSaleVisibleId: string;
  },
): void {
  const rows = db
    .prepare(
      `SELECT client_op_id, payload_json FROM outbox_ops
       WHERE kind = 'refund' AND upload_status IN ('PENDING', 'IN_PROGRESS')`,
    )
    .all() as Array<{ client_op_id: string; payload_json: string }>;

  for (const row of rows) {
    const payload = JSON.parse(row.payload_json) as {
      originalReceiptId?: string;
      originalSaleId?: string;
    };
    let changed = false;
    if (payload.originalReceiptId === mapping.oldReceiptId) {
      payload.originalReceiptId = mapping.newReceiptId;
      changed = true;
    }
    if (payload.originalSaleId === mapping.oldSaleVisibleId) {
      payload.originalSaleId = mapping.newSaleVisibleId;
      changed = true;
    }
    if (changed) {
      db.prepare(
        `UPDATE outbox_ops SET payload_json = ?, cart_id = ? WHERE client_op_id = ?`,
      ).run(JSON.stringify(payload), mapping.newReceiptId, row.client_op_id);
    }
  }
}

export function markReceiptRefundedInHistorySql(
  db: SqliteDatabase,
  originalReceiptId: string,
  originalSaleId?: string,
  status: 'REFUNDED' | 'PARTIAL' = 'REFUNDED',
  refundItems?: Array<{ productId: string; quantity: number }>,
): void {
  const resolved = resolveHistoryReceiptRefs(
    db,
    originalReceiptId,
    originalSaleId || originalReceiptId,
  );
  if (refundItems && refundItems.length > 0) {
    const row = db
      .prepare(
        `SELECT items_json FROM sales_history
         WHERE receipt_number = ? OR sale_visible_id = ?`,
      )
      .get(resolved.receiptNumber, resolved.saleVisibleId) as
      | { items_json: string }
      | undefined;
    if (row) {
      const items = withReturnedQuantities(
        JSON.parse(row.items_json) as OfflineSaleItem[],
        refundItems,
      );
      db.prepare(
        `UPDATE sales_history SET status = ?, items_json = ?
         WHERE receipt_number = ? OR sale_visible_id = ?`,
      ).run(
        status,
        JSON.stringify(items),
        resolved.receiptNumber,
        resolved.saleVisibleId,
      );
      return;
    }
  }
  db.prepare(
    `UPDATE sales_history SET status = ?
     WHERE receipt_number = ? OR sale_visible_id = ?`,
  ).run(status, resolved.receiptNumber, resolved.saleVisibleId);
}
